<script setup lang="ts">
import { inject, nextTick, ref, watch } from "vue";

import { IPilotingWizardState, PilotingWizardStateInjectKey } from "@/components/piloting/wizard/piloting-wizard.types";
import { IProgrammingGanttRow } from "@/components/programming-gantt/programming-gantt";
import { ISelectedSchedule } from "@/components/programming-gantt/programming-gantt.type";
import { IWizardInject, WizardInject } from "@/components/ui/wizard/wizard.types";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";

import Skeleton from "primevue/skeleton";

import ProgrammingGantt from "@/components/programming-gantt/programming-gantt.vue";

interface IProps {
  search?: string;
  selectedApplianceCategory: EProgramApplianceCategory;
}

interface IEmits {
  (e: "refresh-plannings"): void;
}

const props = withDefaults(defineProps<IProps>(), {
  search: "",
});
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();

const programmingGantt = ref();

const wizard = inject<IWizardInject>(WizardInject)!;
const state = inject<IPilotingWizardState>(PilotingWizardStateInjectKey)!;

function selectAll() {
  return programmingGantt.value?.selectAll();
}

function unselectAll() {
  return programmingGantt.value?.unselectAll();
}

function handleEditSchedule(event: ISelectedSchedule) {
  state.formMode = "edit";
  state.maybeSelectedSchedule = event;
  wizard.focusNextTab();
}

defineExpose({
  selectAll,
  unselectAll,
});

watch(
  () => props.search,
  () => {
    nextTick(() => {
      programmingGantt.value?.filter((row: IProgrammingGanttRow) => {
        return row.name.toLowerCase().includes(props.search.toLowerCase());
      });
    });
  },
  { immediate: true },
);
</script>

<template>
  <div class="w-full h-[60vh]">
    <template v-if="state.isPlanningsLoading">
      <Skeleton style="width: 100%; height: 100%" />
    </template>
    <template v-else-if="state.data.rows.length === 0">
      <div class="w-full h-full flex flex-col items-center justify-center">
        <h1 class="text-lg">
          {{ t(k.components.programmingGantt.siteHasNoSection.title) }}
        </h1>
        <span>
          {{ t(k.components.programmingGantt.siteHasNoSection.subtitle) }}
        </span>
      </div>
    </template>
    <template v-else>
      <ProgrammingGantt
        ref="programmingGantt"
        v-model:model-value="state.data"
        v-model:selection="state.selection"
        :plannings="state.plannings"
        :selected-appliance-category="props.selectedApplianceCategory"
        :site-id="state.siteId"
        @edit-planning="handleEditSchedule($event)"
        @refresh-plannings="emits('refresh-plannings')"
      />
    </template>
  </div>
</template>
